
import { useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { LayoutGrid, LayoutList, Download, ExternalLink, RotateCw, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import CollectionCard from "./CollectionCard";
import { useNavigate } from "react-router-dom";
import { useCollections, CollectionWithVideos } from "@/hooks/useCollections";
import { formatDistanceToNow } from "date-fns";

const CollectionsView = () => {
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const { collections, isLoading, error, refetch } = useCollections();
  const navigate = useNavigate();
  
  const handleViewOriginal = (url: string) => {
    window.open(url, '_blank');
  };
  
  const handleReScan = (url: string) => {
    navigate(`/scan?url=${encodeURIComponent(url)}`);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'requested':
        return <Badge variant="outline" className="bg-yellow-900/30 text-yellow-400 border-yellow-700">Pending</Badge>;
      case 'processing':
        return <Badge variant="outline" className="bg-blue-900/30 text-blue-400 border-blue-700">Processing</Badge>;
      case 'completed':
        return <Badge variant="outline" className="bg-green-900/30 text-green-400 border-green-700">Complete</Badge>;
      case 'failed':
        return <Badge variant="outline" className="bg-red-900/30 text-red-400 border-red-700">Failed</Badge>;
      default:
        return <Badge>Unknown</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (error) {
      return 'Unknown date';
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-[#00E5FF] animate-spin mb-4" />
        <p className="text-gray-400">Loading your collection history...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-400 mb-2">Error loading collections: {error}</p>
        <Button variant="outline" onClick={refetch}>
          <RotateCw className="mr-2 h-4 w-4" />
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="py-8 px-4">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-100">Your TikTok Collection History</h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setViewMode('grid')}
            className={viewMode === 'grid' ? "bg-gray-800 text-white" : "bg-transparent text-gray-400"}
          >
            <LayoutGrid className="h-5 w-5" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setViewMode('table')}
            className={viewMode === 'table' ? "bg-gray-800 text-white" : "bg-transparent text-gray-400"}
          >
            <LayoutList className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {collections.length === 0 ? (
        <div className="text-gray-400 text-center py-8">
          <p className="mb-4">No collections available</p>
          <Button 
            variant="default" 
            className="bg-[#00E5FF] hover:bg-[#00E5FF]/90 text-[#111827]"
            onClick={() => navigate('/scan')}
          >
            Scan Your First Collection
          </Button>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {collections.map(collection => (
            <CollectionCard
              key={collection.id}
              title={collection.title || "Untitled Collection"}
              status={collection.status as any}
              videoCount={collection.video_count}
              tiktokUrl={collection.collection_url}
              createdAt={new Date(collection.created_at)}
              zipUrl={collection.status === 'completed' ? `#` : undefined}
              excelUrl={collection.status === 'completed' ? `#` : undefined}
              thumbnailUrl={collection.firstVideoThumbnail}
              onReScan={() => handleReScan(collection.collection_url)}
              onViewOriginal={() => handleViewOriginal(collection.collection_url)}
            />
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-800 rounded-lg">
          <Table>
            <TableHeader className="bg-gray-800/50">
              <TableRow>
                <TableHead>Collection</TableHead>
                <TableHead>Videos</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {collections.map(collection => (
                <TableRow key={collection.id} className="border-gray-800">
                  <TableCell className="font-medium">{collection.title || "Untitled Collection"}</TableCell>
                  <TableCell>{collection.video_count}</TableCell>
                  <TableCell>{getStatusBadge(collection.status)}</TableCell>
                  <TableCell>{formatDate(collection.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleViewOriginal(collection.collection_url)}
                        className="bg-transparent border-gray-700 hover:bg-gray-800"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                      
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleReScan(collection.collection_url)}
                        className="bg-transparent border-gray-700 hover:bg-gray-800"
                      >
                        <RotateCw className="h-4 w-4" />
                      </Button>
                      
                      {collection.status === 'completed' && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="bg-transparent border-gray-700 hover:bg-gray-800"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default CollectionsView;
